import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, setTokens, clearTokens, getAccessToken } from '../lib/api';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'super_admin';
  avatarUrl?: string;
}

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      const token = getAccessToken();
      if (token) {
        try {
          const result = await api.auth.me();
          if (result.success && result.data) {
            const userData = result.data;
            // Only allow admin/super_admin
            if (userData.role === 'admin' || userData.role === 'super_admin') {
              const adminUser: AdminUser = {
                id: userData.id,
                email: userData.email,
                fullName: `${userData.firstName} ${userData.lastName}`,
                role: userData.role as 'admin' | 'super_admin',
                avatarUrl: userData.avatarUrl,
              };
              setUser(adminUser);
              localStorage.setItem('admin_user', JSON.stringify(adminUser));
            } else {
              clearTokens();
            }
          } else {
            clearTokens();
          }
        } catch {
          clearTokens();
        }
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await api.auth.login({ email, password });
    
    if (!result.success || !result.data) {
      throw new Error(result.error?.message || 'Login failed');
    }

    const { user: userData, tokens } = result.data;
    
    // Only allow admin/super_admin
    if (userData.role !== 'admin' && userData.role !== 'super_admin') {
      throw new Error('Access denied. Admin privileges required.');
    }

    setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });

    const adminUser: AdminUser = {
      id: userData.id,
      email: userData.email,
      fullName: `${userData.firstName} ${userData.lastName}`,
      role: userData.role as 'admin' | 'super_admin',
      avatarUrl: userData.avatarUrl,
    };

    setUser(adminUser);
    localStorage.setItem('admin_user', JSON.stringify(adminUser));
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // Ignore logout errors
    }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
